import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Flex,
  Heading,
  Spinner,
  useToast,
  Text,
  UnorderedList,
  ListItem,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import { useCognito } from "../utils/useCognito";
import axios from "axios";
import BAAForm from "../components/BAAForm";
import { useParams, useSearchParams } from "react-router-dom";
import NpiLookupDrawer from "../components/NpiLookupDrawer";

const AdminBAAEditor = () => {
  const { userId } = useParams();
  const { getIdToken } = useCognito();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(true);
  const [updateError, setUpdateError] = useState(null);
  const originalDataRef = useRef(null);
  const [updatedFields, setUpdatedFields] = useState([]);

  const [facilityName, setFacilityName] = useState("");
  const [facilityAddress, setFacilityAddress] = useState({});
  const [shippingContact, setShippingContact] = useState("");
  const [selectedFacility, setSelectedFacility] = useState("");
  const [primaryContactName, setPrimaryContactName] = useState("");
  const [primaryContactEmail, setPrimaryContactEmail] = useState("");
  const [primaryContactPhone, setPrimaryContactPhone] = useState("");
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [salesRepresentative, setSalesRepresentative] = useState("");
  const [physicianInfo, setPhysicianInfo] = useState({});
  const [additionalPhysicians, setAdditionalPhysicians] = useState([]);
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");

  const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  const trackChange = (fieldName, newValue, originalValue) => {
    setUpdatedFields((prev) => {
      const hasChanged = !isEqual(newValue, originalValue);
      const alreadyTracked = prev.includes(fieldName);
      if (hasChanged && !alreadyTracked) return [...prev, fieldName];
      if (!hasChanged && alreadyTracked) return prev.filter((f) => f !== fieldName);
      return prev;
    });
  };

  const parseJSONField = (value, fallback = {}) => {
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return parsed ?? fallback;
      } catch {
        return fallback;
      }
    }
    return value ?? fallback;
  };

  useEffect(() => {
    const fetchBaaData = async () => {
      try {
        const token = getIdToken();
        const res = await axios.post(
          "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getBaaData",
          { userId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const raw = res.data.baaData?.[0] || {};
        const parsed = {
          ...raw,
          facilityAddress: parseJSONField(raw.facilityAddress, {}),
          physicianInfo: parseJSONField(raw.physicianInfo, {}),
          shippingAddresses: parseJSONField(raw.shippingAddresses, []),
          additionalPhysicians: parseJSONField(raw.additionalPhysicians, []),
        };

        // 🔧 Merge top-level facilityFax/Phone into facilityAddress so the form can show them
        const mergedFacilityAddress = {
          ...parsed.facilityAddress,
          phone:
            parsed.facilityPhone ??
            parsed.facilityAddress?.phone ??
            "",
          fax:
            parsed.facilityFax ??
            parsed.facilityAddress?.fax ??
            "",
        };

        // keep original for diffing with highlights
        originalDataRef.current = {
          ...parsed,
          facilityAddress: mergedFacilityAddress,
        };

        setFacilityName(parsed.facilityName || "");
        setFacilityAddress(mergedFacilityAddress);
        setShippingContact(parsed.shippingContact || "");
        setSelectedFacility(parsed.selectedFacility || "");
        setPrimaryContactName(parsed.primaryContactName || "");
        setPrimaryContactEmail(parsed.primaryContactEmail || "");
        setPrimaryContactPhone(parsed.primaryContactPhone || "");
        setShippingAddresses(parsed.shippingAddresses || []);
        setSalesRepresentative(parsed.salesRepresentative || "");
        setPhysicianInfo(parsed.physicianInfo || {});
        setAdditionalPhysicians(parsed.additionalPhysicians || []);
      } catch (err) {
        console.error("Failed to load BAA data", err);
        toast({ title: "Error loading BAA", status: "error", duration: 5000, isClosable: true });
      } finally {
        setLoading(false);
      }
    };
    fetchBaaData();
  }, [userId, getIdToken, toast]);

  const handleUpdate = async () => {
    try {
      const token = getIdToken();

      const formData = {
        facilityName,
        facilityAddress: {
          street: facilityAddress.street || "",
          city: facilityAddress.city || "",
          state: facilityAddress.state || "",
          zip: facilityAddress.zip || "",
          npi: facilityAddress.npi || "",  // legacy
          tin: facilityAddress.tin || "",  // legacy
          ptan: facilityAddress.ptan || "", // legacy
          fax: facilityAddress.fax || "",
          phone: facilityAddress.phone || "",
        },
        // promoted (explicit) — helps backends that store top-level too
        facilityNPI: facilityAddress.npi || "",
        facilityTIN: facilityAddress.tin || "",
        facilityPTAN: facilityAddress.ptan || "",
        facilityFax: facilityAddress.fax || "",
        facilityPhone: facilityAddress.phone || "",

        shippingContact,
        selectedFacility,
        primaryContactName,
        primaryContactEmail,
        primaryContactPhone,
        shippingAddresses,
        salesRepresentative,
        physicianInfo: {
          physicianFirstName: physicianInfo.physicianFirstName || "",
          physicianLastName: physicianInfo.physicianLastName || "",
          phone: physicianInfo.phone || "",
          npi: physicianInfo.npi || "",
          tin: physicianInfo.tin || "",
          ptan: physicianInfo.ptan || "",
          specialty: physicianInfo.specialty || "",
          fax: physicianInfo.fax || "",
          contactName: physicianInfo.contactName || "",
          contactPhone: physicianInfo.contactPhone || "",
          medicareAdminContractor: physicianInfo.medicareAdminContractor || "",
          siteName: physicianInfo.siteName || "",
          street: physicianInfo.street || "",
          city: physicianInfo.city || "",
          state: physicianInfo.state || "",
          zip: physicianInfo.zip || "",
        },
        additionalPhysicians,
      };

      const res = await axios.post(
        "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/updateBaaData",
        { userId, ...formData },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.success) {
        toast({ title: "BAA data updated", status: "success", duration: 4000, isClosable: true });
        const newOriginal = {
          facilityName,
          facilityAddress,
          shippingContact,
          selectedFacility,
          primaryContactName,
          primaryContactEmail,
          primaryContactPhone,
          shippingAddresses,
          salesRepresentative,
          physicianInfo,
          additionalPhysicians,
        };
        originalDataRef.current = JSON.parse(JSON.stringify(newOriginal)); // deep clone
        setUpdatedFields([]);
      } else throw new Error("Update failed");
    } catch (err) {
      console.error("Error saving BAA data:", err);
      toast({ title: "Failed to save BAA", status: "error", duration: 5000, isClosable: true });
    }
  };

  if (loading) {
    return (
      <Flex minH="100vh" justify="center" align="center">
        <Spinner size="xl" />
      </Flex>
    );
  }

  const original = originalDataRef.current;
  const isFormComplete = true;

  return (
    <Box p={6}>
      <Heading size="lg" mb={6}>
        Admin: Edit BAA Data for {email}{" "}
        {userId && <Text as="span" fontSize="md" color="gray.600">({userId})</Text>}
      </Heading>
      <Button onClick={onOpen} _hover={{ transform: "scale(1)" }}>Lookup NPI</Button>
      <NpiLookupDrawer isOpen={isOpen} onClose={onClose} />

      <BAAForm
        facilityName={facilityName}
        setFacilityName={(v) => {
          trackChange("facilityName", v, original?.facilityName);
          setFacilityName(v);
        }}
        facilityAddress={facilityAddress}
        setFacilityAddress={(v) => {
          trackChange("facilityAddress", v, original?.facilityAddress);
          setFacilityAddress(v);
        }}
        shippingContact={shippingContact}
        setShippingContact={(v) => {
          trackChange("shippingContact", v, original?.shippingContact);
          setShippingContact(v);
        }}
        selectedFacility={selectedFacility}
        setSelectedFacility={(v) => {
          trackChange("selectedFacility", v, original?.selectedFacility);
          setSelectedFacility(v);
        }}
        originalData={original}
        primaryContactName={primaryContactName}
        setPrimaryContactName={(v) => {
          trackChange("Primary Contact Name", v, original?.primaryContactName);
          setPrimaryContactName(v);
        }}
        primaryContactEmail={primaryContactEmail}
        setPrimaryContactEmail={(v) => {
          trackChange("Primary Contact Email", v, original?.primaryContactEmail);
          setPrimaryContactEmail(v);
        }}
        primaryContactPhone={primaryContactPhone}
        setPrimaryContactPhone={(v) => {
          trackChange("Primary Contact Phone", v, original?.primaryContactPhone);
          setPrimaryContactPhone(v);
        }}
        shippingAddresses={shippingAddresses}
        setShippingAddresses={(v) => {
          trackChange("Shipping Addresses", v, original?.shippingAddresses);
          setShippingAddresses(v);
        }}
        addShippingAddress={() => {
          const updated = [...shippingAddresses, { street: "", city: "", state: "", zip: "" }];
          trackChange("Shipping Addresses", updated, original?.shippingAddresses);
          setShippingAddresses(updated);
        }}
        updateShippingAddress={(i, key, val) => {
          const updated = shippingAddresses.map((a, idx) => (i === idx ? { ...a, [key]: val } : a));
          trackChange("Shipping Addresses", updated, original?.shippingAddresses);
          setShippingAddresses(updated);
        }}
        deleteShippingAddress={(i) => {
          const updated = shippingAddresses.filter((_, idx) => idx !== i);
          trackChange("Shipping Addresses", updated, original?.shippingAddresses);
          setShippingAddresses(updated);
        }}
        salesRepresentative={salesRepresentative}
        setSalesRepresentative={(v) => {
          trackChange("Sales Representative", v, original?.salesRepresentative);
          setSalesRepresentative(v);
        }}
        physicianInfo={physicianInfo}
        setPhysicianInfo={(v) => {
          trackChange("Physician Info", v, original?.physicianInfo);
          setPhysicianInfo(v);
        }}
        additionalPhysicians={additionalPhysicians}
        addPhysician={() => {
          const updated = [...additionalPhysicians, { firstName: "", lastName: "", specialty: "", npi: "", tin: "", ptan: "", street: "", city: "", state: "", zip: "" }];
          trackChange("Additional Physicians", updated, original?.additionalPhysicians);
          setAdditionalPhysicians(updated);
        }}
        updatePhysician={(index, field, value) => {
          const updated = additionalPhysicians.map((p, i) => (i === index ? { ...p, [field]: value } : p));
          trackChange("Additional Physicians", updated, original?.additionalPhysicians);
          setAdditionalPhysicians(updated);
        }}
        deletePhysician={(index) => {
          const updated = additionalPhysicians.filter((_, i) => i !== index);
          trackChange("Additional Physicians", updated, original?.additionalPhysicians);
          setAdditionalPhysicians(updated);
        }}
        handleSameAsAbove={(index) => {
          const updated = [...additionalPhysicians];
          updated[index] = {
            ...updated[index],
            street: physicianInfo.street,
            city: physicianInfo.city,
            state: physicianInfo.state,
            zip: physicianInfo.zip,
          };
          trackChange("Additional Physicians", updated, original?.additionalPhysicians);
          setAdditionalPhysicians(updated);
        }}
        handleNext={async () => {
          setLoading(true);
          await handleUpdate();
          setLoading(false);
        }}
        isSubmitting={loading}
        updateError={updateError}
        isFormComplete={isFormComplete}
      />

      {updatedFields.length > 0 && (
        <Box mt={8} bg="gray.50" p={4} borderRadius="md">
          <Heading size="sm" mb={2}>Fields Updated:</Heading>
          <UnorderedList>
            {updatedFields.map((field) => (
              <ListItem key={field}>{field}</ListItem>
            ))}
          </UnorderedList>
        </Box>
      )}
    </Box>
  );
};

export default AdminBAAEditor;
